import React, { useMemo, useState } from "react";
import { Ruler, Info } from "lucide-react";
import { exportCalculatorPDF } from "../../lib/pdfExport";
import { SavePdfButton } from "./SavePdfButton";

export default function BMICalc({ lang }) {
  const [heightCm, setHeightCm] = useState("172");
  const [weightKg, setWeightKg] = useState("75");
  const [waistCm, setWaistCm] = useState("90");
  const [sex, setSex] = useState("male");

  const h = parseFloat(heightCm);
  const w = parseFloat(weightKg);
  const waist = parseFloat(waistCm);

  const bmi = useMemo(() => {
    if (!h || !w || h <= 0) return null;
    const v = w / Math.pow(h / 100, 2);
    return Math.round(v * 10) / 10;
  }, [h, w]);

  const bmiCat = useMemo(() => {
    if (bmi === null) return null;
    if (bmi < 18.5) return { label: lang === "et" ? "Alakaal" : lang === "ru" ? "Недостаточная масса" : "Underweight", color: "text-sapphire-700", bg: "bg-sapphire-50", border: "border-sapphire-300" };
    if (bmi < 25) return { label: lang === "et" ? "Normaalne kaal" : lang === "ru" ? "Норма" : "Normal weight", color: "text-teal-600", bg: "bg-teal-200/30", border: "border-teal-300" };
    if (bmi < 30) return { label: lang === "et" ? "Ülekaal" : lang === "ru" ? "Избыточный вес" : "Overweight", color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-300" };
    if (bmi < 35) return { label: lang === "et" ? "Rasvumine I aste" : lang === "ru" ? "Ожирение I" : "Obesity class I", color: "text-orange-600", bg: "bg-orange-50", border: "border-orange-300" };
    if (bmi < 40) return { label: lang === "et" ? "Rasvumine II aste" : lang === "ru" ? "Ожирение II" : "Obesity class II", color: "text-orange-700", bg: "bg-orange-50", border: "border-orange-300" };
    return { label: lang === "et" ? "Rasvumine III aste" : lang === "ru" ? "Ожирение III" : "Obesity class III", color: "text-red-600", bg: "bg-red-50", border: "border-red-300" };
  }, [bmi, lang]);

  const waistCat = useMemo(() => {
    if (!waist) return null;
    const limits = sex === "male" ? { mild: 94, high: 102 } : { mild: 80, high: 88 };
    if (waist < limits.mild) return { label: lang === "et" ? "Madal risk" : lang === "ru" ? "Низкий риск" : "Low risk", color: "text-teal-600" };
    if (waist < limits.high) return { label: lang === "et" ? "Suurenenud risk" : lang === "ru" ? "Повышенный риск" : "Increased risk", color: "text-amber-600" };
    return { label: lang === "et" ? "Kõrge risk" : lang === "ru" ? "Высокий риск" : "High risk", color: "text-red-600" };
  }, [waist, sex, lang]);

  return (
    <section className="card-clinic !p-7" data-testid="bmi-calculator">
      <div className="flex items-center gap-3 mb-5">
        <div className="h-11 w-11 rounded-xl bg-teal-200/40 text-teal-500 flex items-center justify-center">
          <Ruler className="h-6 w-6" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-surface-ink">
            {lang === "et" ? "Kehakaalu ja vööümbermõõdu kalkulaator" : lang === "ru" ? "Калькулятор ИМТ и окружности талии" : "BMI & waist calculator"}
          </h2>
          <p className="text-[13px] text-surface-inkSoft">
            {lang === "et"
              ? "Arvuta oma kehamassiindeks (KMI) ja hinda kõhupiirkonna rasvumise riski."
              : lang === "ru"
              ? "Рассчитайте индекс массы тела (BMI) и оцените риск ожирения по талии."
              : "Calculate your body mass index (BMI) and assess waist-related obesity risk."}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-[13px] font-medium text-surface-ink mb-1.5">
            {lang === "et" ? "Pikkus (cm)" : lang === "ru" ? "Рост (см)" : "Height (cm)"}
          </label>
          <input type="number" min="100" max="230" value={heightCm} onChange={(e) => setHeightCm(e.target.value)} className="input w-full text-[16px] font-medium" data-testid="bmi-height" />
        </div>
        <div>
          <label className="block text-[13px] font-medium text-surface-ink mb-1.5">
            {lang === "et" ? "Kehakaal (kg)" : lang === "ru" ? "Вес (кг)" : "Weight (kg)"}
          </label>
          <input type="number" min="30" max="300" value={weightKg} onChange={(e) => setWeightKg(e.target.value)} className="input w-full text-[16px] font-medium" data-testid="bmi-weight" />
        </div>
        <div>
          <label className="block text-[13px] font-medium text-surface-ink mb-1.5">
            {lang === "et" ? "Vööümbermõõt (cm)" : lang === "ru" ? "Окружность талии (см)" : "Waist (cm)"}
          </label>
          <input type="number" min="40" max="200" value={waistCm} onChange={(e) => setWaistCm(e.target.value)} className="input w-full text-[16px] font-medium" data-testid="bmi-waist" />
        </div>
      </div>

      <div className="mt-3 inline-flex bg-surface-canvas rounded-lg p-1 gap-1 text-[13px]" role="radiogroup">
        <button
          type="button"
          onClick={() => setSex("male")}
          data-testid="bmi-sex-male"
          className={`px-4 py-1.5 rounded-md font-medium transition-colors ${sex === "male" ? "bg-white text-surface-ink shadow" : "text-surface-inkSoft hover:text-surface-ink"}`}
        >
          {lang === "et" ? "Mees" : lang === "ru" ? "Мужчина" : "Male"}
        </button>
        <button
          type="button"
          onClick={() => setSex("female")}
          data-testid="bmi-sex-female"
          className={`px-4 py-1.5 rounded-md font-medium transition-colors ${sex === "female" ? "bg-white text-surface-ink shadow" : "text-surface-inkSoft hover:text-surface-ink"}`}
        >
          {lang === "et" ? "Naine" : lang === "ru" ? "Женщина" : "Female"}
        </button>
      </div>

      {bmi && bmiCat && (
        <div className={`mt-5 rounded-xl border ${bmiCat.border} ${bmiCat.bg} p-4 grid grid-cols-1 sm:grid-cols-2 gap-4`} data-testid="bmi-result">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-surface-inkSoft">KMI / BMI</div>
            <div className="text-3xl font-bold text-surface-ink">{bmi.toFixed(1)}</div>
            <div className={`text-[14px] font-semibold mt-1 ${bmiCat.color}`}>{bmiCat.label}</div>
          </div>
          {waistCat && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-surface-inkSoft">
                {lang === "et" ? "Vööümbermõõt — risk" : lang === "ru" ? "Талия — риск" : "Waist — risk"}
              </div>
              <div className="text-3xl font-bold text-surface-ink">{waist} <span className="text-base font-medium">cm</span></div>
              <div className={`text-[14px] font-semibold mt-1 ${waistCat.color}`}>{waistCat.label}</div>
            </div>
          )}
        </div>
      )}

      {bmi && bmiCat && (
        <SavePdfButton
          lang={lang}
          testId="bmi-save-pdf"
          onClick={() =>
            exportCalculatorPDF({
              lang,
              title:
                lang === "et"
                  ? "KMI ja vööümbermõõdu kalkulaator"
                  : lang === "ru"
                  ? "Калькулятор ИМТ и талии"
                  : "BMI and waist calculator",
              inputs: [
                {
                  label: lang === "et" ? "Pikkus" : lang === "ru" ? "Рост" : "Height",
                  value: `${h} cm`,
                },
                {
                  label: lang === "et" ? "Kehakaal" : lang === "ru" ? "Вес" : "Weight",
                  value: `${w} kg`,
                },
                {
                  label: lang === "et" ? "Vööümbermõõt" : lang === "ru" ? "Окружность талии" : "Waist",
                  value: `${waist} cm`,
                },
                {
                  label: lang === "et" ? "Sugu" : lang === "ru" ? "Пол" : "Sex",
                  value:
                    sex === "male"
                      ? lang === "et"
                        ? "Mees"
                        : lang === "ru"
                        ? "Мужчина"
                        : "Male"
                      : lang === "et"
                      ? "Naine"
                      : lang === "ru"
                      ? "Женщина"
                      : "Female",
                },
              ],
              results: [
                { label: "KMI / BMI", value: bmi.toFixed(1), hint: bmiCat.label },
                ...(waistCat
                  ? [
                      {
                        label:
                          lang === "et"
                            ? "Vööümbermõõt — risk"
                            : lang === "ru"
                            ? "Талия — риск"
                            : "Waist — risk",
                        value: `${waist} cm`,
                        hint: waistCat.label,
                      },
                    ]
                  : []),
              ],
              note:
                lang === "et"
                  ? "KMI on lihtne hindamisvahend, kuid ei eralda lihasmassi rasvkoest. Sportlastel ja eakatel võib see anda eksitavaid tulemusi."
                  : lang === "ru"
                  ? "ИМТ — простой показатель, но не различает мышечную массу и жир."
                  : "BMI is a simple tool that does not distinguish muscle from fat.",
            })
          }
        />
      )}

      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4 text-[12px]">
        <div className="rounded-xl bg-surface-canvas p-3">
          <div className="font-semibold text-surface-ink mb-1.5">KMI {lang === "et" ? "kategooriad" : lang === "ru" ? "категории" : "categories"}</div>
          <ul className="space-y-0.5 text-surface-inkSoft">
            <li>&lt; 18.5 — {lang === "et" ? "alakaal" : "underweight"}</li>
            <li>18.5–24.9 — {lang === "et" ? "normaalne" : "normal"}</li>
            <li>25.0–29.9 — {lang === "et" ? "ülekaal" : "overweight"}</li>
            <li>≥ 30.0 — {lang === "et" ? "rasvumine" : "obesity"}</li>
          </ul>
        </div>
        <div className="rounded-xl bg-surface-canvas p-3">
          <div className="font-semibold text-surface-ink mb-1.5">{lang === "et" ? "Vööümbermõõt — risk (WHO)" : lang === "ru" ? "Талия — риск (WHO)" : "Waist — risk (WHO)"}</div>
          <ul className="space-y-0.5 text-surface-inkSoft">
            <li>{lang === "et" ? "Mees" : "Male"}: &lt; 94 / 94–101 / ≥ 102 cm</li>
            <li>{lang === "et" ? "Naine" : "Female"}: &lt; 80 / 80–87 / ≥ 88 cm</li>
          </ul>
        </div>
      </div>

      <div className="mt-5 flex items-start gap-2.5 rounded-xl bg-teal-200/15 border border-teal-300/40 p-4 text-[14px] leading-relaxed text-surface-ink">
        <Info className="h-5 w-5 text-teal-500 mt-0.5 shrink-0" />
        <p>
          {lang === "et"
            ? "KMI on lihtne hindamisvahend, kuid ei eralda lihasmassi rasvkoest. Sportlastel ja eakatel võib see anda eksitavaid tulemusi."
            : lang === "ru"
            ? "BMI — простой показатель, но не различает мышечную массу и жир."
            : "BMI is a simple tool that does not distinguish muscle from fat. Athletes and elderly may need different interpretation."}
        </p>
      </div>
    </section>
  );
}
